/**
 * /app/enrolments — firm-side inbox of every ClientOps enrolment.
 * Shows completed subscribers first (with plan + amount), plus abandoned
 * drafts so staff can chase them up. Powered by GET /api/enrol/admin/list.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { Receipt, ExternalLink, Trash2, Loader2 } from "lucide-react";

const PLAN_COLOUR = {
    sole_trader: "bg-emerald-50 text-emerald-700 border-emerald-200",
    company_2owner: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const STATUS_COLOUR = {
    completed: "bg-emerald-100 text-emerald-800",
    checkout_started: "bg-amber-100 text-amber-800",
    authority: "bg-amber-100 text-amber-800",
    service: "bg-zinc-100 text-zinc-700",
    basics: "bg-zinc-100 text-zinc-700",
    draft: "bg-zinc-100 text-zinc-700",
    cancelled: "bg-rose-100 text-rose-800",
};

function fmtAmount(cents) {
    if (!cents) return "—";
    return `NZ$${(cents / 100).toFixed(2)}`;
}
function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
}

export default function Enrolments() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [data, setData] = useState({ rows: [], summary: {} });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [convertingId, setConvertingId] = useState(null);
    const [xeroBusyId, setXeroBusyId] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [deleting, setDeleting] = useState(false);

    async function load(showSpinner = true) {
        if (showSpinner) setLoading(true);
        try {
            const params = filter ? { status: filter } : {};
            const { data: resp } = await api.get("/enrol/admin/list", { params });
            setData(resp);
            setSelected((prev) => {
                const valid = new Set((resp.rows || []).map((r) => r.id));
                const next = new Set();
                prev.forEach((id) => { if (valid.has(id)) next.add(id); });
                return next;
            });
        } finally {
            if (showSpinner) setLoading(false);
        }
    }

    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await load(true); })();
        const t = setInterval(() => load(false), 60000);
        return () => { cancelled = true; clearInterval(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    async function convertToFirmClient(enrolmentId) {
        setConvertingId(enrolmentId);
        try {
            const { data: resp } = await api.post(`/enrol/${enrolmentId}/convert-to-firm-client`);
            toast.success(
                resp.already_converted
                    ? "Already a firm-managed client — opening now"
                    : `Converted — ${resp.moved_docs} document${resp.moved_docs === 1 ? "" : "s"} and ${resp.moved_requests} request${resp.moved_requests === 1 ? "" : "s"} moved`,
            );
            await load(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not convert this enrolment");
        } finally {
            setConvertingId(null);
        }
    }

    async function createXeroInvoice(enrolmentId) {
        setXeroBusyId(enrolmentId);
        try {
            const { data: resp } = await api.post(`/enrol/${enrolmentId}/xero-repeating-invoice`);
            toast.success(
                resp.already_created
                    ? "Repeating invoice already set up in Xero"
                    : `Repeating invoice created in Xero (${resp.xero_status || "AUTHORISED"})`,
            );
            await load(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create Xero invoice");
        } finally {
            setXeroBusyId(null);
        }
    }

    function copyIrdAndOpenMyir(row) {
        const ird = (row.ird_number || "").trim();
        if (!ird) {
            toast.error("No IRD number captured for this client");
            return;
        }
        try {
            navigator.clipboard.writeText(ird);
            toast.success(`IRD ${ird} copied — paste it in myIR`);
        } catch {
            toast.info(`IRD is ${ird} — copy it manually`);
        }
        window.open("https://myir.ird.govt.nz/", "_blank", "noopener,noreferrer");
    }

    const mrrDollars = useMemo(() => (data.summary?.mrr_completed_cents || 0) / 100, [data.summary]);

    const selectedRows = useMemo(() => (data.rows || []).filter((r) => selected.has(r.id)), [data.rows, selected]);
    const blockedRows = useMemo(() => selectedRows.filter((r) => r.status === "completed" && r.firm_client_id), [selectedRows]);
    const deletableRows = useMemo(() => selectedRows.filter((r) => !(r.status === "completed" && r.firm_client_id)), [selectedRows]);

    async function confirmDelete() {
        if (deleteConfirm !== "DELETE") { toast.error('Type "DELETE" to confirm'); return; }
        setDeleting(true);
        try {
            const { data: resp } = await api.post("/enrol/admin/bulk-delete", {
                confirm: deleteConfirm,
                enrolment_ids: selectedRows.map((r) => r.id),
            });
            const skipped = resp.blocked?.length || 0;
            toast.success(`Deleted ${resp.deleted} enrolment${resp.deleted === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (already converted)` : ""}`);
            setDeleteOpen(false);
            setDeleteConfirm("");
            setSelected(new Set());
            await load(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div data-testid="enrolments-page">
            <header className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">NZ Taxation Limited</div>
                    <h1 className="text-2xl font-semibold text-[#0F1115]">Enrolments</h1>
                    <p className="text-[13px] text-[#5E636E] mt-1">Every ClientOps self-serve signup — welcome them personally the day they land.</p>
                </div>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="enrolments-summary">
                <Card className="p-4">
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">Paid last 7 days</div>
                    <div className="text-2xl font-semibold text-[#0F1115] mt-1" data-testid="summary-completed-7d">{data.summary?.completed_7d || 0}</div>
                    <div className="text-[11px] text-[#5E636E] mt-1">
                        {(data.summary?.sole_trader_7d || 0)} sole trader · {(data.summary?.company_2owner_7d || 0)} company
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">All completed</div>
                    <div className="text-2xl font-semibold text-[#0F1115] mt-1" data-testid="summary-completed-all">{data.summary?.completed || 0}</div>
                </Card>
                <Card className="p-4">
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">In progress</div>
                    <div className="text-2xl font-semibold text-amber-700 mt-1" data-testid="summary-in-progress">{data.summary?.in_progress || 0}</div>
                    <div className="text-[11px] text-[#5E636E] mt-1">Drafts to chase</div>
                </Card>
                <Card className="p-4">
                    <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">Monthly recurring</div>
                    <div className="text-2xl font-semibold text-[#0F1115] mt-1" data-testid="summary-mrr">NZ${mrrDollars.toFixed(2)}</div>
                    <div className="text-[11px] text-[#5E636E] mt-1">Across paid subscribers</div>
                </Card>
            </div>

            <div className="mb-4 flex items-center gap-2 text-[13px]">
                {[
                    { k: "", label: "All" },
                    { k: "completed", label: "Paid" },
                    { k: "checkout_started", label: "At checkout" },
                    { k: "authority", label: "Signed AoA" },
                    { k: "service", label: "Details filled" },
                    { k: "basics", label: "Basics only" },
                    { k: "draft", label: "Drafts" },
                    { k: "cancelled", label: "Cancelled" },
                ].map((opt) => (
                    <button
                        key={opt.k || "all"}
                        onClick={() => setFilter(opt.k)}
                        className={`rounded-full px-3 py-1 border transition-colors ${
                            filter === opt.k
                                ? "bg-[#0F1115] text-white border-[#0F1115]"
                                : "bg-white text-[#5E636E] border-[#E4E4E7] hover:text-[#0F1115]"
                        }`}
                        data-testid={`enrolments-filter-${opt.k || "all"}`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <Card className="p-0 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]" data-testid="enrolments-table">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wider text-[#5E636E] border-b border-[#E4E4E7]">
                                {isAdmin && (
                                    <th className="px-3 py-3 w-8">
                                        <input
                                            type="checkbox"
                                            aria-label="Select all enrolments"
                                            checked={data.rows?.length > 0 && selected.size === data.rows.length}
                                            ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < (data.rows?.length || 0); }}
                                            onChange={(e) => {
                                                if (e.target.checked) setSelected(new Set((data.rows || []).map((r) => r.id)));
                                                else setSelected(new Set());
                                            }}
                                            className="h-3.5 w-3.5 accent-[#0F1115] cursor-pointer"
                                            data-testid="enrolments-select-all"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Plan</th>
                                <th className="px-4 py-3">Contact</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Amount / mo</th>
                                <th className="px-4 py-3">Started</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="enrolments-tbody">
                            {loading && (
                                <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-6 text-center text-[#5E636E]">Loading…</td></tr>
                            )}
                            {!loading && (data.rows || []).length === 0 && (
                                <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-6 text-center text-[#5E636E]">No enrolments yet in this bucket.</td></tr>
                            )}
                            {(data.rows || []).map((r) => (
                                <tr key={r.id} className={`border-b border-[#F1F1F1] hover:bg-[#FAFAF7] ${selected.has(r.id) ? "bg-red-50/40" : ""}`} data-testid={`enrolment-row-${r.id}`}>
                                    {isAdmin && (
                                        <td className="px-3 py-3">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${r.full_name || r.email || r.id}`}
                                                checked={selected.has(r.id)}
                                                onChange={(e) => {
                                                    setSelected((prev) => {
                                                        const next = new Set(prev);
                                                        if (e.target.checked) next.add(r.id); else next.delete(r.id);
                                                        return next;
                                                    });
                                                }}
                                                className="h-3.5 w-3.5 accent-[#0F1115] cursor-pointer"
                                                data-testid={`enrolments-select-${r.id}`}
                                            />
                                        </td>
                                    )}
                                    <td className="px-4 py-3 font-medium text-[#0F1115]">{r.full_name || <span className="text-[#5E636E]">—</span>}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${PLAN_COLOUR[r.service] || ""}`}>
                                            {r.plan_label || r.service}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-[#1F2328]">
                                        <div>{r.email || <span className="text-[#5E636E]">—</span>}</div>
                                        <div className="text-[#5E636E] text-[12px]">{r.mobile || ""}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_COLOUR[r.status] || "bg-zinc-100"}`}>
                                            {r.status}
                                        </span>
                                        {r.firm_client_id && (
                                            <div className="mt-1 text-[10px] uppercase tracking-wider text-emerald-700" data-testid={`enrolment-firm-flag-${r.id}`}>
                                                Firm-managed
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#0F1115]">{fmtAmount(r.amount_cents)}</td>
                                    <td className="px-4 py-3 text-[#5E636E]">{fmtDate(r.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        {r.status !== "completed" ? (
                                            <span className="text-[12px] text-[#B4B4BC]">—</span>
                                        ) : r.firm_client_id ? (
                                            <div className="flex flex-col items-end gap-1.5">
                                                <Link
                                                    to={`/app/clients/${r.firm_client_id}`}
                                                    className="inline-block text-[12px] font-medium text-[#0F1115] hover:underline"
                                                    data-testid={`enrolment-open-client-${r.id}`}
                                                >
                                                    Open client →
                                                </Link>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant={r.xero_repeating_invoice_id ? "outline" : "outline"}
                                                        className="h-7 px-2 text-[11px] gap-1"
                                                        disabled={xeroBusyId === r.id || !!r.xero_repeating_invoice_id}
                                                        onClick={() => createXeroInvoice(r.id)}
                                                        title={r.xero_repeating_invoice_id ? `Xero RepeatingInvoice ${r.xero_repeating_invoice_id.slice(0,8)}…` : "Create a monthly repeating invoice in Xero Blue"}
                                                        data-testid={`enrolment-xero-${r.id}`}
                                                    >
                                                        <Receipt className="h-3 w-3" />
                                                        {xeroBusyId === r.id ? "Creating…" : r.xero_repeating_invoice_id ? "Xero ✓" : "Xero invoice"}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 px-2 text-[11px] gap-1"
                                                        disabled={!r.ird_number}
                                                        onClick={() => copyIrdAndOpenMyir(r)}
                                                        title={r.ird_number ? `Copy IRD ${r.ird_number} and open myIR` : "No IRD captured"}
                                                        data-testid={`enrolment-myir-${r.id}`}
                                                    >
                                                        <ExternalLink className="h-3 w-3" />
                                                        Copy IRD → myIR
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-[12px] h-8"
                                                disabled={convertingId === r.id}
                                                onClick={() => convertToFirmClient(r.id)}
                                                data-testid={`enrolment-convert-${r.id}`}
                                            >
                                                {convertingId === r.id ? "Converting…" : "Convert to firm client"}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {isAdmin && selected.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full border border-[#0F1115] bg-[#0F1115] text-white shadow-xl px-4 py-2" data-testid="enrolments-bulk-bar">
                    <span className="text-[13px] font-medium">{selected.size} enrolment{selected.size === 1 ? "" : "s"} selected</span>
                    <span className="h-4 w-px bg-white/20" />
                    <button onClick={() => setSelected(new Set())} className="text-[12px] text-white/70 hover:text-white" data-testid="enrolments-bulk-clear">Clear</button>
                    <Button size="sm" onClick={() => { setDeleteConfirm(""); setDeleteOpen(true); }} className="h-7 text-[12px] bg-red-600 hover:bg-red-700 text-white" data-testid="enrolments-bulk-delete">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected
                    </Button>
                </div>
            )}

            {isAdmin && (
                <Dialog open={deleteOpen} onOpenChange={(v) => !deleting && setDeleteOpen(v)}>
                    <DialogContent data-testid="enrolments-delete-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-red-800">Delete {selected.size} enrolment{selected.size === 1 ? "" : "s"}?</DialogTitle>
                            <DialogDescription>
                                <b>THIS CANNOT BE UNDONE.</b> Deletes the enrolment row and any linked draft data (documents, welcome emails).
                            </DialogDescription>
                        </DialogHeader>
                        <div className="max-h-40 overflow-y-auto rounded-md border border-red-200 bg-red-50/40 p-2 space-y-1 text-[12.5px]" data-testid="enrolments-delete-list">
                            {deletableRows.map((r) => (
                                <div key={r.id} className="text-red-900">• {r.full_name || r.email || r.id} <span className="text-red-700/70">({r.status})</span></div>
                            ))}
                            {blockedRows.length > 0 && (
                                <div className="mt-2 border-t border-red-200 pt-2 text-[11.5px] text-amber-800">
                                    <div className="font-semibold mb-1">Will be skipped ({blockedRows.length}):</div>
                                    {blockedRows.map((r) => (
                                        <div key={r.id}>• {r.full_name || r.email || r.id} — already converted to a firm client; cancel the Stripe subscription first</div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[12.5px] font-medium text-[#0F1115]">Type &quot;DELETE&quot; to confirm</label>
                            <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" data-testid="enrolments-delete-confirm" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
                            <Button onClick={confirmDelete} disabled={deleting || deleteConfirm !== "DELETE" || deletableRows.length === 0} className="bg-red-700 hover:bg-red-800 text-white" data-testid="enrolments-delete-submit">
                                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Delete {deletableRows.length} row{deletableRows.length === 1 ? "" : "s"}</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
